import axios from 'axios';
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { AuthUtils } from 'src/services/Auth/Auth.utils';
import { GlobalHttpAbortController } from './GlobalHttpAbortController';
import { AxiosHttpError, InterceptableAxiosRequestConfig, isCancelError } from './Http.types';
import { HttpError } from './HttpError';
import { Logger } from 'src/utils/logger';
import { errorHandlingService } from 'src/services/error-handling';

import { API_URL } from 'src/env.json';
import { toast } from 'src/components/ui/use-toast';

// Retry configuration
const MAX_RETRY_ATTEMPTS = 1;
const TOKEN_REFRESH_WAIT_MS = 5000; // Wait up to 5 seconds for mobile app to send new token
const VISIBILITY_CHANGE_GRACE_PERIOD_MS = 15000; // 15 seconds after tab becomes visible
const STALE_REQUEST_THRESHOLD_MS = 30000; // Consider requests stale after 30 seconds of tab being hidden

/**
 * HTTP State Manager - manages shared state for Http instances
 * Using a class with static properties instead of module-level variables
 * to ensure proper cleanup during HMR and testing
 */
class HttpState {
  // Token refresh state
  static isRefreshingToken = false;
  static refreshTokenPromise: Promise<string | null> | null = null;

  // Logout state
  static isLoggingOut = false;

  // Tab visibility tracking
  static lastVisibilityChangeTime = 0;
  static lastHiddenTime = 0;
  static wasHiddenBefore = false;
  static visibilityListenerAttached = false;

  /**
   * Initialize visibility change listener (idempotent)
   */
  static initVisibilityListener(): void {
    if (this.visibilityListenerAttached || typeof document === 'undefined') {
      return;
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        HttpState.wasHiddenBefore = true;
        HttpState.lastHiddenTime = Date.now();
        Logger.debug('Tab hidden - tracking time for stale request detection', 'Http');
      } else if (document.visibilityState === 'visible' && HttpState.wasHiddenBefore) {
        HttpState.lastVisibilityChangeTime = Date.now();
        const hiddenDuration = Date.now() - HttpState.lastHiddenTime;
        Logger.debug(
          `Tab became visible after ${Math.round(hiddenDuration / 1000)}s - starting ${VISIBILITY_CHANGE_GRACE_PERIOD_MS / 1000}s error suppression grace period`,
          'Http',
        );
      }
    });

    this.visibilityListenerAttached = true;
  }

  /**
   * Check if within visibility grace period
   */
  static isWithinVisibilityGracePeriod(): boolean {
    return Date.now() - this.lastVisibilityChangeTime < VISIBILITY_CHANGE_GRACE_PERIOD_MS;
  }

  /**
   * Check if a request is stale
   */
  static isStaleRequest(requestStartTime?: number): boolean {
    if (!requestStartTime || !this.lastHiddenTime) return false;

    if (requestStartTime < this.lastHiddenTime) {
      return true;
    }

    const hiddenDuration = (this.lastVisibilityChangeTime || Date.now()) - this.lastHiddenTime;
    if (
      hiddenDuration > STALE_REQUEST_THRESHOLD_MS &&
      requestStartTime < this.lastVisibilityChangeTime
    ) {
      return true;
    }

    return false;
  }

  /**
   * Reset all state - useful for testing
   */
  static reset(): void {
    this.isRefreshingToken = false;
    this.refreshTokenPromise = null;
    this.isLoggingOut = false;
    this.lastVisibilityChangeTime = 0;
    this.lastHiddenTime = 0;
    this.wasHiddenBefore = false;
  }
}

// Initialize visibility listener
HttpState.initVisibilityListener();

class Http {
  axios: AxiosInstance;

  post: AxiosInstance['post'];

  get: AxiosInstance['get'];

  put: AxiosInstance['put'];

  patch: AxiosInstance['patch'];

  delete: AxiosInstance['delete'];

  baseUrl = API_URL;

  constructor() {
    const defaultHeaders = {
      Accept: 'application/json',
      'Content-Type': 'application/json;charset=utf-8',
      'Access-Control-Expose-Headers': 'Access-Control-*',
      'Access-Control-Allow-Headers':
        'Access-Control-*, Origin, X-Requested-With, Content-Type, Accept',
      'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS, HEAD',
      'Access-Control-Allow-Origin': '*',
      Allow: 'GET, POST, PUT, DELETE, OPTIONS, HEAD',
      crossdomain: true,
    };

    this.axios = axios.create({
      baseURL: this.baseUrl,
      timeout: 20000,
      withCredentials: true,
      headers: defaultHeaders,
      signal: GlobalHttpAbortController.signal,
      paramsSerializer: {
        serialize: (params) => {
          // Custom serializer that preserves null values in arrays
          const searchParams = new URLSearchParams();

          const serialize = (obj: any, prefix: string = '') => {
            if (obj === null || obj === undefined) {
              // Keep null/undefined values
              searchParams.append(prefix, '');
            } else if (Array.isArray(obj)) {
              // Handle arrays - preserve null values
              obj.forEach((value, index) => {
                serialize(value, `${prefix}[${index}]`);
              });
            } else if (typeof obj === 'object' && obj !== null) {
              // Handle nested objects
              Object.keys(obj).forEach((key) => {
                serialize(obj[key], prefix ? `${prefix}[${key}]` : key);
              });
            } else {
              // Primitive values
              searchParams.append(prefix, String(obj));
            }
          };

          serialize(params);
          return searchParams.toString();
        },
      },
    });

    this.post = this.axios.post;
    this.get = this.axios.get;
    this.put = this.axios.put;
    this.patch = this.axios.patch;
    this.delete = this.axios.delete;

    // Request interceptor: Always get fresh token before each request
    this.axios.interceptors.request.use((request: InternalAxiosRequestConfig) => {
      // Track request start time for stale request detection
      (request as any)._requestStartTime = Date.now();

      // Get fresh token for each request (important for webview where token can be updated)
      if (!AuthUtils.isExpired()) {
        request.headers.set('Authorization', `Bearer ${AuthUtils.getJwt().token}`);
      }
      return request;
    }, this.errorInterceptor);

    // Response interceptor: Handle 401 with retry after waiting for token refresh
    this.axios.interceptors.response.use(
      (response: AxiosResponse) => {
        return response.data;
      },
      async (error: AxiosHttpError) => {
        const originalRequest = error.config as InterceptableAxiosRequestConfig & {
          _retryCount?: number;
        };

        // Check if this is a 401 error and we haven't exceeded retry attempts
        // Skip token refresh for auth endpoints - 401 means invalid credentials, not expired token
        const requestUrl = originalRequest?.url || '';
        const isAuthEndpoint = ['/auth/login', '/auth/social-login', '/auth/register'].some(
          (endpoint) => requestUrl.includes(endpoint),
        );

        if (
          error.response?.status === 401 &&
          originalRequest &&
          !isAuthEndpoint &&
          (originalRequest._retryCount || 0) < MAX_RETRY_ATTEMPTS
        ) {
          originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;

          Logger.info(
            `Got 401, waiting for token refresh (attempt ${originalRequest._retryCount})`,
            'Http',
          );

          try {
            // Refresh token using the auth endpoint
            const newToken = await this.refreshToken();

            if (newToken && originalRequest.headers) {
              Logger.info('Token refreshed, retrying request', 'Http');
              // Update the authorization header with new token
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              // Retry the request
              return this.axios.request(originalRequest);
            }
          } catch (retryError) {
            Logger.error('Token refresh failed', 'Http', retryError);
          }
        }

        // If not a 401 or retry failed, use the standard error interceptor
        return this.errorInterceptor(error);
      },
    );
  }

  setBearer(accessToken: string): void {
    this.axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  }

  /**
   * Refresh the access token.
   * In webview: requests token from mobile app and waits for it.
   * In web: calls the auth refresh endpoint directly.
   */
  private refreshToken = async (): Promise<string | null> => {
    // If already refreshing, wait for the existing promise
    if (HttpState.isRefreshingToken && HttpState.refreshTokenPromise) {
      return HttpState.refreshTokenPromise;
    }

    HttpState.isRefreshingToken = true;
    const isWebView = typeof window !== 'undefined' && !!(window as any).ReactNativeWebView;

    if (isWebView) {
      // In webview: request token from mobile app and wait
      Logger.info('Requesting token refresh from mobile app', 'Http');
      HttpState.refreshTokenPromise = this.requestTokenFromMobile();
    } else {
      // In web: call refresh endpoint directly
      Logger.info('Refreshing access token via API', 'Http');
      HttpState.refreshTokenPromise = this.refreshTokenViaApi();
    }

    return HttpState.refreshTokenPromise;
  };

  /**
   * Request token refresh from mobile app and wait for response.
   */
  private requestTokenFromMobile = async (): Promise<string | null> => {
    const originalToken = AuthUtils.getJwt().token;

    try {
      // Send request to mobile app
      (window as any).ReactNativeWebView.postMessage(
        JSON.stringify({
          type: 'REQUEST_TOKEN_REFRESH',
          secure: true,
          timestamp: Date.now(),
        }),
      );

      // Wait for mobile app to update the token
      return new Promise<string | null>((resolve) => {
        const startTime = Date.now();
        const checkInterval = 100; // Check every 100ms

        const checkToken = () => {
          const currentToken = AuthUtils.getJwt().token;
          const elapsed = Date.now() - startTime;

          // Token changed and not expired - refresh successful
          if (currentToken && currentToken !== originalToken && !AuthUtils.isExpired()) {
            Logger.info('New token received from mobile app', 'Http');
            HttpState.isRefreshingToken = false;
            HttpState.refreshTokenPromise = null;
            resolve(currentToken);
            return;
          }

          // Timeout reached
          if (elapsed >= TOKEN_REFRESH_WAIT_MS) {
            Logger.warn(`Token refresh wait timed out after ${TOKEN_REFRESH_WAIT_MS}ms`, 'Http');
            HttpState.isRefreshingToken = false;
            HttpState.refreshTokenPromise = null;
            resolve(null);
            return;
          }

          // Keep checking
          setTimeout(checkToken, checkInterval);
        };

        // Start checking
        checkToken();
      });
    } catch (error) {
      Logger.error('Failed to request token from mobile', 'Http', error);
      HttpState.isRefreshingToken = false;
      HttpState.refreshTokenPromise = null;
      return null;
    }
  };

  /**
   * Refresh token via API (for web browser, not webview).
   */
  private refreshTokenViaApi = async (): Promise<string | null> => {
    try {
      const currentToken = AuthUtils.getJwt().token;
      const response = await axios.post(
        `${this.baseUrl}/auth/refresh`,
        {},
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            ...(currentToken ? { Authorization: `Bearer ${currentToken}` } : {}),
          },
        },
      );

      const accessToken = response.data?.access_token;

      if (accessToken) {
        const authObj = AuthUtils.make(accessToken);
        AuthUtils.setJwt(authObj);
        this.setBearer(accessToken);
        Logger.info('Token refreshed successfully via API', 'Http');
        return accessToken;
      }

      Logger.warn('No access token in refresh response', 'Http');
      return null;
    } catch (error) {
      Logger.error('Failed to refresh token via API', 'Http', error);
      return null;
    } finally {
      HttpState.isRefreshingToken = false;
      HttpState.refreshTokenPromise = null;
    }
  };

  /**
   * Detects aborted/timed-out requests (ECONNABORTED).
   * These are typically caused by user navigation or network interruption.
   * Fixes CUSTOMER-WEB-APP-P3
   */
  private isAbortError = (error: AxiosHttpError): boolean => {
    return (
      error.code === 'ECONNABORTED' ||
      error.message?.toLowerCase().includes('request aborted') === true
    );
  };

  errorInterceptor = (axiosError: AxiosHttpError): Promise<HttpError> => {
    const httpError = new HttpError(axiosError);
    const requestStartTime = (axiosError.config as any)?._requestStartTime;

    // During logout, reject with a cancellation-like error that won't trigger error handlers
    if (HttpState.isLoggingOut) {
      const cancelError = new HttpError({
        ...axiosError,
        code: 'ERR_CANCELED',
        message: 'Request cancelled during logout',
      } as AxiosHttpError);
      return Promise.reject(cancelError);
    }

    // Fall back to axiosError.config when there's no response — network
    // errors (DNS, offline, CORS preflight) carry the request config on
    // `axiosError.config` only, so reading `response.config` alone would
    // miss `_noIntercept` and fire the global toast anyway.
    const interceptConfig = (axiosError.response?.config ?? axiosError.config) as
      | InterceptableAxiosRequestConfig
      | undefined;
    if (
      !isCancelError(httpError) &&
      !this.isAbortError(axiosError) &&
      !interceptConfig?._noIntercept
    ) {
      // Don't show toast for 401/403 auth errors (handled by retry logic or during logout)
      // Use == to handle both number and string codes
      // 401 = Unauthorized, 403 = Forbidden (both can occur during logout/session expiry)
      if (httpError.code != 401 && httpError.code != 403) {
        this.setToastError(httpError, requestStartTime);
      }
    }

    return Promise.reject(httpError);
  };

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  convertObjectToQueryString = (params: any): string => {
    return Object.keys(params)
      .map((key) => `${key}=${params[key]}`)
      .join('&');
  };

  /**
   * Set logout state to suppress error toasts during logout process.
   */
  setLoggingOut(value: boolean): void {
    HttpState.isLoggingOut = value;
  }

  private setToastError = (error: HttpError, requestStartTime?: number): void => {
    // Skip 422 validation errors (handled by forms)
    if (error.code === 422) {
      return;
    }

    // Suppress all error toasts during logout to prevent "User is not logged in" messages
    if (HttpState.isLoggingOut) {
      return;
    }

    // Check if this is a generic server/network error
    const isGenericServerError =
      error.code === 500 ||
      error.code === 502 ||
      error.code === 503 ||
      error.code === 504 ||
      error.code === 0 || // Network failure
      error.code === 'ERR_NETWORK' ||
      error.message?.toLowerCase().includes('server error') ||
      error.message?.toLowerCase().includes('network error') ||
      error.message?.toLowerCase().includes('failed to fetch') ||
      error.message?.toLowerCase().includes('connection') ||
      error.message === 'Unknown error';

    // STRATEGY 1: Suppress stale requests (started before/during tab hidden)
    // These are likely to fail due to session expiration or network state changes
    if (HttpState.isStaleRequest(requestStartTime) && isGenericServerError) {
      Logger.info('Suppressed stale request error (started before/during tab hidden)', 'Http', {
        code: error.code,
        message: error.message,
        requestAge: requestStartTime
          ? Math.round((Date.now() - requestStartTime) / 1000)
          : 'unknown',
      });
      return;
    }

    // STRATEGY 2: Suppress generic errors during visibility grace period
    // This handles errors from requests that started right when user returned
    if (HttpState.isWithinVisibilityGracePeriod() && isGenericServerError) {
      Logger.info('Suppressed background refetch error during visibility grace period', 'Http', {
        code: error.code,
        message: error.message,
        timeInGracePeriod: Math.round((Date.now() - HttpState.lastVisibilityChangeTime) / 1000),
      });
      return;
    }

    // STRATEGY 3: Suppress generic errors shortly after long hidden periods
    // User was away for a long time (>5min), suppress generic errors for first 15s back
    if (HttpState.lastHiddenTime && HttpState.lastVisibilityChangeTime) {
      const hiddenDuration = HttpState.lastVisibilityChangeTime - HttpState.lastHiddenTime;
      const timeSinceVisible = Date.now() - HttpState.lastVisibilityChangeTime;

      if (hiddenDuration > 300000 && timeSinceVisible < 15000 && isGenericServerError) {
        Logger.info(
          `Suppressed error after long absence (${Math.round(hiddenDuration / 60000)}min)`,
          'Http',
          { code: error.code, message: error.message },
        );
        return;
      }
    }

    // Use error handling service to get user-friendly messages
    const processed = errorHandlingService.processError(error, {
      showToast: false, // We handle toast ourselves
      suppressLog: true, // Already logged above
    });

    // Show user-friendly error toast
    toast({
      description: processed.message,
      title: processed.title,
      variant: 'destructive',
    });
  };
}

export default Http;
